import {
  Zap,
  Server,
  ShieldCheck,
  Cpu,
  RefreshCw,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  Coins,
} from "lucide-react";
import { Section, SectionHeading, Card } from "./service-revops-ui";
import { SectionCta } from "./service-revops-section-cta";

/**
 * n8n-workflow-developer exclusive:
 * 1. Self-Hosted vs Zapier Financial & Volume Comparison Module
 * 2. Zero-Drop Error-Handling & Dead-Letter Queue Governance Stack
 * Self-contained, reverse-engineered from top n8n agencies (Goodspeed, n8n Lab, Luhhu).
 */

const costComparison = [
  {
    icon: Zap,
    platform: "Zapier / Cloud No-Code",
    pricingModel: "$0.02 – $0.05 per task",
    limitations:
      "100k tasks/mo costs $600–$1,500/mo. Brittle linear flows, no custom JS packages, silent failure drops.",
    status: "Expensive & Fragile",
    statusBad: true,
  },
  {
    icon: Server,
    platform: "Self-Hosted n8n Orchestration",
    pricingModel: "Flat $20–$50/mo VPS Server",
    limitations:
      "Unlimited execution volume with zero per-task bills. Custom JavaScript nodes, Docker isolation, 100% data sovereignty.",
    status: "Production-Grade & Scalable",
    statusBad: false,
  },
];

const deadLetterSteps = [
  {
    icon: AlertTriangle,
    step: "01",
    title: "Error Trigger Intercept",
    desc: "The instant an external API 5xxs or times out, the Error Trigger node intercepts the error payload before execution drops.",
  },
  {
    icon: RefreshCw,
    step: "02",
    title: "Exponential Backoff Retry",
    desc: "Automated retry cycles fire at 30s, 5m, and 15m intervals to recover from temporary network or rate-limit blips.",
  },
  {
    icon: ShieldCheck,
    step: "03",
    title: "Dead-Letter Queue Serialization",
    desc: "If retries exhaust, the malformed payload is serialized and stored in PostgreSQL/Redis with a full audit trace for manual replay.",
  },
  {
    icon: Cpu,
    step: "04",
    title: "Real-Time Telemetry & Alerting",
    desc: "Instant Slack/Teams notification sent to your team with direct execution ID links and payload diagnostic context.",
  },
];

const roiMetrics = [
  {
    label: "Execution Volume",
    bad: "Capped by task pricing",
    good: "Unlimited on VPS",
  },
  {
    label: "Logic Capability",
    bad: "Basic if/then branches",
    good: "Custom JavaScript + npm packages",
  },
  {
    label: "Error Handling",
    bad: "Silent drops & lost leads",
    good: "Error Trigger + Dead-Letter Queue",
  },
  {
    label: "Monthly SaaS Bill",
    bad: "$800 – $2,500+ / mo",
    good: "Flat $20 – $50 / mo server",
  },
];

export function ServiceN8nComparison() {
  return (
    <>
      {/* §3b.1 — Self-Hosted vs Zapier Financial & Volume Comparison */}
      <Section>
        <SectionHeading
          eyebrow="FINANCIAL & CAPACITY ROI"
          navy="Self-Hosted n8n vs."
          blue="Fragile Zapier Task Taxes."
          sub="Stop paying a penalty for growing your business. Self-hosted n8n gives you code-level freedom and unlimited executions for a flat server cost."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {costComparison.map((c) => {
            const Icon = c.icon;
            return (
              <Card key={c.platform} radius={24}>
                <div className="flex items-center justify-between mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: c.statusBad
                        ? "linear-gradient(135deg, #D9534F, #A93226)"
                        : "linear-gradient(135deg, #0B7CF2, #0860BE)",
                    }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span
                    className="font-sans font-bold uppercase tracking-[0.14em] px-3 py-1 rounded-full text-[11px]"
                    style={{
                      background: c.statusBad
                        ? "rgba(217,83,79,0.1)"
                        : "rgba(39,220,93,0.1)",
                      color: c.statusBad ? "#D9534F" : "#169846",
                      border: `1px solid ${
                        c.statusBad
                          ? "rgba(217,83,79,0.2)"
                          : "rgba(39,220,93,0.2)"
                      }`,
                    }}
                  >
                    {c.status}
                  </span>
                </div>
                <h3
                  className="font-display font-bold mb-2"
                  style={{
                    fontSize: "20px",
                    color: "#071E61",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {c.platform}
                </h3>
                <div
                  className="font-display font-extrabold mb-3"
                  style={{
                    fontSize: "26px",
                    color: c.statusBad ? "#7A2E2B" : "#0B7CF2",
                  }}
                >
                  {c.pricingModel}
                </div>
                <p
                  style={{
                    fontSize: "14.5px",
                    lineHeight: 1.55,
                    color: "#536B89",
                  }}
                >
                  {c.limitations}
                </p>
              </Card>
            );
          })}
        </div>

        {/* ROI comparison table */}
        <Card>
          <div className="flex items-center gap-2 mb-6">
            <Coins className="w-5 h-5" style={{ color: "#0B7CF2" }} />
            <span
              className="font-sans font-bold uppercase tracking-[0.14em]"
              style={{ fontSize: "12px", color: "#0B7CF2" }}
            >
              Task-Taxed SaaS vs Self-Hosted Architecture
            </span>
          </div>
          <div className="space-y-1">
            {roiMetrics.map((r) => (
              <div
                key={r.label}
                className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-3 md:gap-4 py-3.5 border-b border-[rgba(8,46,103,0.06)] last:border-0"
              >
                <span
                  className="font-display font-bold"
                  style={{ fontSize: "15px", color: "#071E61" }}
                >
                  {r.label}
                </span>
                <AlertTriangle
                  className="hidden md:block w-4 h-4 shrink-0"
                  style={{ color: "#D9534F" }}
                />
                <span style={{ fontSize: "14px", color: "#8C5A57" }}>
                  {r.bad}
                </span>
                <ArrowRight
                  className="hidden md:block w-4 h-4 shrink-0"
                  style={{ color: "#0B7CF2" }}
                />
                <span
                  className="flex items-center gap-2 font-medium"
                  style={{ fontSize: "14px", color: "#169846" }}
                >
                  <CheckCircle2
                    className="w-4 h-4 shrink-0"
                    style={{ color: "#27DC5D" }}
                  />
                  {r.good}
                </span>
              </div>
            ))}
          </div>
        </Card>
        <SectionCta
          copy="Ready to slash your automation SaaS bill? Let's plan your n8n migration."
          label="Plan My Self-Hosted Build"
        />
      </Section>

      {/* §3b.2 — Zero-Drop Dead-Letter Queue Governance Stack */}
      <Section>
        <SectionHeading
          eyebrow="RELIABILITY GOVERNANCE"
          navy="Architecting Zero-Drop"
          blue="Dead-Letter Queue Resilience."
          sub="No silent drops, no lost leads at 3 AM. I engineer 4-tier error resilience into every n8n workflow so temporary outage never causes data loss."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {deadLetterSteps.map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.title} radius={24}>
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: "linear-gradient(135deg, #0B7CF2, #0860BE)",
                    }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span
                    className="font-display font-black select-none"
                    style={{
                      fontSize: "44px",
                      lineHeight: 1,
                      color: "rgba(17,143,247,0.16)",
                      letterSpacing: "-0.05em",
                    }}
                  >
                    {s.step}
                  </span>
                </div>
                <h3
                  className="font-display font-bold mb-2"
                  style={{
                    fontSize: "20px",
                    color: "#071E61",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {s.title}
                </h3>
                <p
                  style={{
                    fontSize: "14.5px",
                    lineHeight: 1.55,
                    color: "#536B89",
                  }}
                >
                  {s.desc}
                </p>
              </Card>
            );
          })}
        </div>
        <SectionCta
          copy="Want zero-drop reliability on your critical API pipelines?"
          label="Engineer My Error Stack"
        />
      </Section>
    </>
  );
}
